// frontend/src/components/Website/Footer.tsx
import React, { useState } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { newsletterAPI } from '../../services/api';
import './Footer.css';

const Footer: React.FC = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const currentYear = new Date().getFullYear();

  const menuItems = [
    { name: 'Home', link: 'home' },
    { name: 'Services', link: 'services' },
    { name: 'Technologies', link: 'technologies' },
    { name: 'Why Choose Us', link: 'why-choose-us' },
    { name: 'About', link: 'about' },
    { name: 'Portfolio', link: 'portfolio' },
    { name: 'Contact', link: 'contact' },
  ];

  const socialLinks = [
    { name: 'Facebook', url: 'https://facebook.com', icon: (
      <svg className="social-svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
      </svg>
    )},
    { name: 'Twitter', url: 'https://twitter.com', icon: (
      <svg className="social-svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
      </svg>
    )},
    { name: 'LinkedIn', url: 'https://linkedin.com', icon: (
      <svg className="social-svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
        <rect x="2" y="9" width="4" height="12"/>
        <circle cx="4" cy="4" r="2"/>
      </svg>
    )},
    { name: 'GitHub', url: 'https://github.com', icon: (
      <svg className="social-svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
      </svg>
    )},
    { name: 'Instagram', url: 'https://instagram.com', icon: (
      <svg className="social-svg" viewBox="0 0 24 24" fill="currentColor">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
      </svg>
    )},
  ];

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous messages
    setError('');
    setShowSuccess(false);
    
    // Validate email
    if (!newsletterEmail) {
      setError('Please enter your email address');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    if (!validateEmail(newsletterEmail)) {
      setError('Please enter a valid email address');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the Django backend API - send only the email
      const response = await newsletterAPI.subscribe({ 
        email: newsletterEmail.trim()
      });
      
      // Show success message
      setShowSuccess(true);
      setNewsletterEmail('');
      
      // Clear success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
      
      // Optional: Show additional info from response
      if (response.data.message) {
        console.log('Subscription response:', response.data.message);
      }
      
    } catch (err: any) {
      console.error('Newsletter subscription error:', err);
      
      // Handle different error scenarios
      if (err.response) {
        // Server responded with error
        const errorData = err.response.data;
        let errorMessage = 'Failed to subscribe. Please try again.';
        
        // Try to extract error message from different possible formats
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.email) {
          // If the error is about the email field
          errorMessage = Array.isArray(errorData.email) ? errorData.email[0] : errorData.email;
        } else if (errorData?.detail) {
          errorMessage = errorData.detail;
        }
        
        // Check if email already subscribed
        if (errorMessage.toLowerCase().includes('already') || 
            errorMessage.toLowerCase().includes('exists') ||
            errorMessage.toLowerCase().includes('subscribed')) {
          setError('This email is already subscribed to our newsletter!');
        } else {
          setError(errorMessage);
        }
      } else if (err.request) {
        // Request was made but no response
        setError('Network error. Please check your connection and try again.');
      } else {
        // Something else happened
        setError('An unexpected error occurred. Please try again later.');
      }
      
      // Clear error after 5 seconds
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-particle footer-particle-1"></div>
      <div className="footer-particle footer-particle-2"></div>
      <div className="footer-particle footer-particle-3"></div>
      
      <div className="footer-container">
        <div className="footer-grid">
          {/* Company Info */}
          <div className="footer-section">
            <h3 className="footer-title">ZiTech Solutions</h3>
            <p className="footer-description">
              Empowering organizations through cutting-edge technology that drives growth and operational excellence.
            </p>
            <div className="footer-contact-info">
              <p>
                <svg className="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                +255 768 249 444
              </p>
              <p>
                <svg className="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                info@zitechsolutions.com
              </p>
              <p>
                <svg className="contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                Mwanza, Tanzania
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h3 className="footer-title">Quick Links</h3>
            <ul className="footer-links">
              {menuItems.map((item) => (
                <li key={item.name} className="footer-link-item">
                  <ScrollLink
                    to={item.link}
                    spy={true}
                    smooth={true}
                    duration={800}
                    offset={-70}
                    className="footer-link"
                  >
                    {item.name}
                  </ScrollLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Support & Legal */}
          <div className="footer-section">
            <h3 className="footer-title">Support</h3>
            <ul className="footer-links">
              <li className="footer-link-item">
                <a href="/support" className="footer-link">Support Center</a>
              </li>
              <li className="footer-link-item">
                <a href="/blog" className="footer-link">Blog</a>
              </li>
              <li className="footer-link-item">
                <a href="/careers" className="footer-link">Careers</a>
              </li>
              <li className="footer-link-item">
                <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
              </li>
              <li className="footer-link-item">
                <a href="/terms-of-service" className="footer-link">Terms of Service</a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="footer-section">
            <h3 className="footer-title">Newsletter</h3>
            <p className="footer-description">
              Subscribe to get updates on our latest news and offers.
            </p>
            
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <div className="newsletter-input-wrapper">
                <input
                  type="email"
                  className={`newsletter-input ${error ? 'error' : ''} ${showSuccess ? 'success' : ''}`}
                  placeholder="Your email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                />
                <button 
                  type="submit" 
                  className="newsletter-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
            </form>
            
            {/* Success Message */}
            {showSuccess && (
              <div className="newsletter-success">
                <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Subscribed successfully! Please check your email to confirm.</span>
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div className="newsletter-error">
                <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            
            <div className="social-icons">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  className="social-icon"
                  aria-label={social.name}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p className="footer-copyright">
            © {currentYear} ZiTech Solutions. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;